'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type UseQueryStateOptions<T> = {
  default: T;
  parse?: (raw: string) => T;
  serialize?: (value: T) => string;
};

const defaultParse = <T>(raw: string): T => raw as T;
const defaultSerialize = (value: unknown): string => String(value);

/**
 * * Syncs a single query parameter with the URL. Uses optimistic local state so
 * * the UI updates immediately on setValue; URL is updated with router.replace()
 * * and kept in sync. When the value equals the default, the param is omitted.
 */
export function useQueryState<T>(
  key: string,
  options: UseQueryStateOptions<T>,
): [T, (value: T) => void] {
  const { default: defaultValue, parse, serialize } = options;
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();

  const valueFromUrl = useMemo(() => {
    const raw = searchParams.get(key);
    if (raw === null) return defaultValue;

    const parseFn = parse ?? defaultParse;
    return parseFn(raw);
  }, [searchParams, key, defaultValue, parse]);

  const [value, setValueState] = useState<T>(valueFromUrl);
  const pendingValueRef = useRef<T | null>(null);
  const hasPendingValueRef = useRef(false);

  useEffect(() => {
    if (hasPendingValueRef.current) {
      if (Object.is(valueFromUrl, pendingValueRef.current)) {
        hasPendingValueRef.current = false;
        pendingValueRef.current = null;
      } else {
        // Ignore stale URL snapshots until router/searchParams catch up.
        return;
      }
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setValueState(valueFromUrl);
  }, [valueFromUrl]);

  const setValue = useCallback(
    (next: T) => {
      setValueState(next);
      hasPendingValueRef.current = true;
      pendingValueRef.current = next;
      // * Use current browser URL so multiple setValue calls in one tick (e.g. setTableFilters)
      // * merge correctly instead of the second overwriting the first with stale searchParams.
      const currentQuery =
        typeof window !== 'undefined'
          ? window.location.search.slice(1)
          : searchParams.toString();

      const nextParams = new URLSearchParams(currentQuery);
      const serialized = (serialize ?? defaultSerialize)(next);
      const isDefault = next === defaultValue;

      if (isDefault) {
        nextParams.delete(key);
      } else {
        nextParams.set(key, serialized);
      }

      const query = nextParams.toString();
      const url = query ? `${pathname}?${query}` : pathname;
      router.replace(url);

      if (typeof window !== 'undefined') {
        window.history.replaceState(undefined, '', url);
      }
    },
    [pathname, searchParams, router, key, defaultValue, serialize],
  );

  return [value, setValue];
}
